import { useState } from "react";

interface ContactFormProps {
  formId: string;
  submitUrl: string;
  formName: string;
}

type Status = "idle" | "submitting" | "success" | "error";

const subjects = ["内容合作", "影视作品推荐", "资讯纠错与反馈", "线下放映合作", "其他咨询"];
const maxMessage = 500;

export default function ContactForm({ formId, submitUrl, formName }: ContactFormProps) {
  const [status, setStatus] = useState<Status>("idle");
  const [errorText, setErrorText] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const guard = String(formData.get("company_alt") ?? "").trim();
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = String(value);
      if (!text) return;
      payload.append(key, text);
    });

    if (guard) {
      setStatus("success");
      form.reset();
      setLength(0);
      return;
    }

    if (String(formData.get("message") ?? "").length > maxMessage) {
      setStatus("error");
      setErrorText(`留言内容不能超过 ${maxMessage} 字，请精简后再提交。`);
      return;
    }

    setStatus("submitting");
    setErrorText("");

    try {
      const response = await fetch(submitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后再试";
      const isSpam = /spam/i.test(serverMsg);

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setLength(0);
      } else {
        setStatus("error");
        setErrorText(serverMsg || "提交失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setErrorText("网络好像不太稳定，请稍后再试一次。");
    }
  };

  return (
    <div id={formId} className="bg-background-100 rounded-lg p-5 md:p-7">
      <div className="mb-5">
        <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-2">联系我们</h2>
        <p className="text-sm text-foreground-500 leading-relaxed">
          合作洽谈、内容建议或放映邀约，都可以在这里留言。我们会在两个工作日内回复。
        </p>
      </div>

      {status === "success" ? (
        <div className="py-10 text-center">
          <span className="inline-flex w-14 h-14 rounded-full bg-accent-100 text-accent-700 items-center justify-center mb-4">
            <i className="ri-mail-check-line text-2xl w-6 h-6 flex items-center justify-center"></i>
          </span>
          <h3 className="font-heading text-lg text-foreground-950 mb-2">感谢你的留言</h3>
          <p className="text-sm text-foreground-500 mb-5">我们已经收到了，编辑部会尽快查看并回复。</p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="h-10 px-5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
          >
            再写一条
          </button>
        </div>
      ) : (
        <form data-readdy-form id={`${formId}-form`} name={formName} onSubmit={handleSubmit} className="space-y-4">
          <input type="hidden" name="form_name" value={formName} />
          <input
            type="text"
            name="company_alt"
            className="contact-guard"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`${formId}-name`} className="block text-xs text-foreground-600 mb-1.5">
                称呼 <span className="text-accent-700">*</span>
              </label>
              <input
                id={`${formId}-name`}
                name="name"
                type="text"
                required
                maxLength={40}
                placeholder="怎么称呼你"
                className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>
            <div>
              <label htmlFor={`${formId}-email`} className="block text-xs text-foreground-600 mb-1.5">
                邮箱 <span className="text-accent-700">*</span>
              </label>
              <input
                id={`${formId}-email`}
                name="email"
                type="email"
                required
                maxLength={80}
                placeholder="name@example.com"
                className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
              />
            </div>
          </div>
          <div>
            <label htmlFor={`${formId}-subject`} className="block text-xs text-foreground-600 mb-1.5">
              留言主题
            </label>
            <select
              id={`${formId}-subject`}
              name="subject"
              defaultValue={subjects[0]}
              className="w-full h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
            >
              {subjects.map((subject) => (
                <option key={subject} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor={`${formId}-message`} className="block text-xs text-foreground-600">
                留言内容 <span className="text-accent-700">*</span>
              </label>
              <span className={`text-[11px] ${length > maxMessage ? "text-accent-700" : "text-foreground-400"}`}>
                {length} / {maxMessage}
              </span>
            </div>
            <textarea
              id={`${formId}-message`}
              name="message"
              required
              rows={5}
              maxLength={maxMessage}
              onChange={(event) => setLength(event.target.value.length)}
              placeholder="写下你想说的事，不超过 500 字"
              className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400 resize-none"
            ></textarea>
          </div>

          {status === "error" && (
            <p className="text-xs text-accent-800 bg-accent-50 border border-accent-200 rounded-md px-3 py-2 leading-relaxed">
              {errorText}
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === "submitting" ? "提交中…" : "提交留言"}
            </button>
            <p className="text-[11px] text-foreground-400 leading-relaxed">提交即表示同意我们通过邮箱与你联系，信息仅用于回复本次留言。</p>
          </div>
        </form>
      )}
    </div>
  );
}